import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, formatDate, formatIDR } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { RiskBadge, StatusBadge, ScorePill, STATUS_LABELS } from "../components/badges";
import ClaimDetailSheet from "../components/ClaimDetailSheet";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { MagnifyingGlass, FilePdf, FileCsv, PencilSimple, Trash } from "@phosphor-icons/react";
import { DatePickerID } from "../components/DatePickerID";
import EditClaimDialog from "../components/EditClaimDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALL = "__all__";

export default function ClaimsQueuePage() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [filters, setFilters] = useState({ claim_type: ALL, status: ALL, risk_category: ALL, q: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [editClaim, setEditClaim] = useState(null);

  const removeClaim = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`Hapus klaim ${c.claim_number} beserta dokumennya? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      const { data } = await api.delete(`/claims/${c.id}`);
      toast.success(data.message);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const now = new Date();
  const [recap, setRecap] = useState({
    start: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`,
    end: now.toISOString().slice(0, 10),
  });
  const isInvestigator = ["investigator_internal", "investigator_eksternal"].includes(user.role);
  const canRecap = ["admin", "supervisor"].includes(user.role);

  const load = useCallback(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
    api.get("/claims", { params }).then((r) => setClaims(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="p-8" data-testid="claims-queue-page">
      <h1 className="text-2xl font-semibold tracking-tight">{isInvestigator ? "Tugas Saya" : "Antrean Klaim"}</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">
        {isInvestigator ? "Klaim yang ditugaskan kepada Anda" : "Seluruh klaim beserta skor dan status penanganan"}
      </p>

      {canRecap && (
        <div className="flex flex-wrap items-center gap-3 mb-4 border border-border bg-card px-4 py-3" data-testid="recap-bar">
          <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Rekap Periode</span>
          <div className="w-44"><DatePickerID testId="recap-start-date" value={recap.start} onChange={(v) => setRecap((s) => ({ ...s, start: v }))} /></div>
          <span className="text-muted-foreground text-sm">s/d</span>
          <div className="w-44"><DatePickerID testId="recap-end-date" value={recap.end} onChange={(v) => setRecap((s) => ({ ...s, end: v }))} /></div>
          <a data-testid="recap-pdf-button" href={`${BACKEND}/api/reports/recap?start=${recap.start}&end=${recap.end}&format=pdf`}
            onClick={(e) => { if (recap.start > recap.end) { e.preventDefault(); toast.error("Tanggal awal harus sebelum tanggal akhir"); } }}
            className={`inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-medium hover:border-slate-900 hover:text-slate-900 transition-colors duration-150 ${recap.start > recap.end ? "opacity-50" : ""}`}>
            <FilePdf size={15} /> Rekap PDF
          </a>
          <a data-testid="recap-csv-button" href={`${BACKEND}/api/reports/recap?start=${recap.start}&end=${recap.end}&format=csv`}
            onClick={(e) => { if (recap.start > recap.end) { e.preventDefault(); toast.error("Tanggal awal harus sebelum tanggal akhir"); } }}
            className={`inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs font-medium hover:border-slate-900 hover:text-slate-900 transition-colors duration-150 ${recap.start > recap.end ? "opacity-50" : ""}`}>
            <FileCsv size={15} /> Rekap CSV
          </a>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="filter-search" className="pl-9 w-64" placeholder="Cari no. klaim / polis / pelapor"
            value={filters.q} onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))} />
        </div>
        <Select value={filters.claim_type} onValueChange={(v) => setFilters((s) => ({ ...s, claim_type: v }))}>
          <SelectTrigger data-testid="filter-claim-type" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Semua Jenis</SelectItem>
            <SelectItem value="jiwa">Jiwa</SelectItem>
            <SelectItem value="kendaraan">Kendaraan</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.risk_category} onValueChange={(v) => setFilters((s) => ({ ...s, risk_category: v }))}>
          <SelectTrigger data-testid="filter-risk" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Semua Risiko</SelectItem>
            <SelectItem value="rendah">Rendah</SelectItem>
            <SelectItem value="sedang">Sedang</SelectItem>
            <SelectItem value="tinggi">Tinggi</SelectItem>
            <SelectItem value="kritis">Kritis</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters((s) => ({ ...s, status: v }))}>
          <SelectTrigger data-testid="filter-status" className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Semua Status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-5 py-3 font-medium">No. Klaim</th>
              <th className="px-3 py-3 font-medium">Jenis</th>
              <th className="px-3 py-3 font-medium">Pelapor</th>
              <th className="px-3 py-3 font-medium">Provinsi</th>
              <th className="px-3 py-3 font-medium">Nilai Klaim</th>
              <th className="px-3 py-3 font-medium">Skor</th>
              <th className="px-3 py-3 font-medium">Risiko</th>
              <th className="px-3 py-3 font-medium">Status</th>
              <th className="px-3 py-3 font-medium">Investigator</th>
              <th className="px-3 py-3 font-medium">Tanggal</th>
              {canRecap && <th className="px-3 py-3 font-medium w-20">Aksi</th>}
            </tr>
          </thead>
          <tbody>
            {claims.length === 0 && (
              <tr><td colSpan={10} className="px-5 py-10 text-center text-muted-foreground" data-testid="empty-claims">Tidak ada klaim ditemukan</td></tr>
            )}
            {claims.map((c) => (
              <tr key={c.id} data-testid={`claim-row-${c.claim_number}`} onClick={() => setSelectedId(c.id)}
                className="border-b border-border/60 last:border-0 hover:bg-accent cursor-pointer transition-colors duration-150">
                <td className="px-5 py-3 font-mono text-xs font-medium">{c.claim_number}</td>
                <td className="px-3 py-3 capitalize">{c.claim_type}</td>
                <td className="px-3 py-3">{c.reporter_name}</td>
                <td className="px-3 py-3">{c.province}</td>
                <td className="px-3 py-3 font-mono text-xs">{formatIDR(c.claim_amount)}</td>
                <td className="px-3 py-3"><ScorePill score={c.score} /></td>
                <td className="px-3 py-3"><RiskBadge category={c.risk_category} /></td>
                <td className="px-3 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-3 py-3 text-xs">{c.assigned_to_name || "—"}</td>
                <td className="px-3 py-3 text-xs text-muted-foreground">{formatDate(c.created_at)}</td>
                {canRecap && (
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <button data-testid={`edit-claim-${c.claim_number}`} title="Edit klaim"
                        onClick={(e) => { e.stopPropagation(); setEditClaim(c); }}
                        className="text-muted-foreground hover:text-slate-900 transition-colors duration-150"><PencilSimple size={16} /></button>
                      <button data-testid={`delete-claim-${c.claim_number}`} title="Hapus klaim"
                        onClick={(e) => removeClaim(e, c)}
                        className="text-muted-foreground hover:text-red-600 transition-colors duration-150"><Trash size={16} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ClaimDetailSheet claimId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />
      <EditClaimDialog claim={editClaim} onClose={() => setEditClaim(null)} onSaved={load} />
    </div>
  );
}
